<template>
  <section
    class="growth-section growth-faq-section"
    aria-labelledby="landing-faq-title"
  >
    <div class="growth-faq-intro">
      <h2 id="landing-faq-title">
        {{ $t('landing.faq-title') }}
      </h2>
      <div class="growth-glossary-callout">
        <h3>{{ $t('glossary.callout-title') }}</h3>
        <p>{{ $t('glossary.callout-subtitle') }}</p>
        <router-link
          to="/distribution-glossary"
          class="growth-text-link"
        >
          {{ $t('glossary.callout-cta') }}<GrowthIcon name="arrow" />
        </router-link>
      </div>
    </div>
    <div class="growth-faq-list">
      <article
        v-for="(faq, index) in faqs"
        :key="faq.question"
        class="growth-faq-item"
      >
        <h3>
          <button
            :id="`faq-question-${index}`"
            type="button"
            :aria-expanded="activeIndex === index"
            :aria-controls="`faq-answer-${index}`"
            @click="toggleFaq(index)"
          >
            <span>{{ $t(faq.question) }}</span><GrowthIcon :name="activeIndex === index ? 'close' : 'plus'" />
          </button>
        </h3>
        <div
          v-show="activeIndex === index"
          :id="`faq-answer-${index}`"
          class="growth-faq-answer"
          role="region"
          :aria-labelledby="`faq-question-${index}`"
        >
          <p>{{ $t(faq.answer) }}</p>
        </div>
      </article>
    </div>
  </section>
</template>
<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '@vueuse/head';
import GrowthIcon from '@/components/growth/GrowthIcon.vue';

const { t } = useI18n();

const activeIndex = ref(0);

const faqs = [
  {
    question: 'landing.faq-item1-question',
    answer: 'landing.faq-item1-answer',
  },
  {
    question: 'landing.faq-item2-question',
    answer: 'landing.faq-item2-answer',
  },
  {
    question: 'landing.faq-item3-question',
    answer: 'landing.faq-item3-answer',
  },
  {
    question: 'landing.faq-item4-question',
    answer: 'landing.faq-item4-answer',
  },
  {
    question: 'landing.faq-item5-question',
    answer: 'landing.faq-item5-answer',
  },
  {
    question: 'landing.faq-item6-question',
    answer: 'landing.faq-item6-answer',
  },
];

const faqJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: t(faq.question),
    acceptedAnswer: {
      '@type': 'Answer',
      text: t(faq.answer),
    },
  })),
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      key: 'faq-page-jsonld',
      children: computed(() => JSON.stringify(faqJsonLd.value)),
    },
  ],
});

const toggleFaq = (index) => {
  if (activeIndex.value === index) {
    activeIndex.value = -1;
  } else {
    activeIndex.value = index;
  }
};
</script>
<style scoped>
@reference '../../styles/refresh.css';
.growth-faq-section { @apply tw:grid tw:items-start;grid-template-columns:minmax(0,.85fr) minmax(0,1.15fr);gap:clamp(40px,6vw,120px); }
.growth-faq-intro h2 { font-size:clamp(34px,3.5vw,64px);line-height:1.08;letter-spacing:-.055em;max-width:10em; }
.growth-glossary-callout { margin-top:36px;max-width:400px; }
.growth-glossary-callout h3 { font-size:18px;line-height:1.4; }
.growth-glossary-callout p { font-size:14px;line-height:1.8;color:var(--growth-muted,#62665d);margin:12px 0; }
.growth-glossary-callout a { font-size:13px; }
.growth-faq-item { border-bottom:1px solid var(--growth-line,#e0e3d9);padding:24px 0; }
.growth-faq-item:first-child { padding-top:0; }
.growth-faq-item button { @apply tw:flex tw:items-center tw:justify-between tw:gap-6; width:100%;min-height:44px;padding:0;border:0;background:none;color:inherit;text-align:left;font-size:clamp(16px,1.1vw,21px);font-weight:550;line-height:1.5;cursor:pointer; }
.growth-faq-item button svg { width:20px;height:20px;color:var(--growth-muted,#62665d); }
.growth-faq-answer p { margin:14px 0 0;font-size:clamp(14px,1vw,17px);line-height:1.85;color:var(--growth-muted,#62665d);max-width:65ch; }
@media(max-width:800px) {
  .growth-faq-section { grid-template-columns:minmax(0,1fr);gap:32px; }
  .growth-faq-intro h2 { max-width:14em; }
  .growth-glossary-callout { margin-top:24px; }
}
</style>
